import { useEffect, useRef } from 'react';
import { draftField, useDraft } from '../../offline/draft.js';
import { X } from 'lucide-react';
import { SUPPORT_TYPES } from '@nu/shared';
import { requestSupport } from './support.js';

function Choice({ n, label, options, value, onChange }) {
  return (
    <div className="popup-field">
      <span className="q-head"><span className="q-n" aria-hidden="true">{n}</span><span>{label} <span className="req">(required)</span></span></span>
      <div className="reason-chips left" role="radiogroup" aria-label={label}>
        {options.map((o) => <button key={o} type="button" role="radio" aria-checked={value === o} className={`chip${value === o ? ' on' : ''}`} onClick={() => onChange(o)}>{o}</button>)}
      </div>
    </div>
  );
}

// Ask for support during the works: their supervisor (with a reason), or a
// waste tipper for the spoil, and notes. The team's supervisor
// (from Match-Ups) and operations are told at once and arrange it.
export default function RequestSupport({ job, onClose }) {
  const ref = useRef(null);
  // Kept on the phone until it's sent or cancelled.
  const [draft, saveDraft, clearDraft] = useDraft(`nu.draft.support.${job.id}`);
  const [type, setType] = draftField(draft, saveDraft, 'type', '');
  const [notes, setNotes] = draftField(draft, saveDraft, 'notes', '');
  useEffect(() => {
    ref.current?.focus();
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  const why = type === 'Supervisor';
  const ready = type && (!why || notes.trim().length >= 3);
  const send = async () => {
    await requestSupport(job, { type, notes: notes.trim() });
    clearDraft();
    onClose();
  };
  const sup = job.supervisor;

  return (
    <div className="popup-backdrop" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="popup popup-form" role="dialog" aria-modal="true" aria-labelledby="support-title" tabIndex={-1} ref={ref}>
        <button type="button" className="popup-x" aria-label="Close" onClick={onClose}><X size={20} aria-hidden="true" /></button>
        <p className="popup-kicker">{job.reference} · {[job.addressLine1, job.postcode].filter(Boolean).join(', ')}</p>
        <h2 id="support-title" className="popup-title">Request support</h2>

        <hr className="q-rule" />
        <Choice n={1} label="What do you need?" options={SUPPORT_TYPES} value={type} onChange={setType} />

        <hr className="q-rule" />
        <label className="popup-field">
          <span className="q-head"><span className="q-n" aria-hidden="true">2</span><span>{why ? 'Why do you need your supervisor?' : 'Notes'} {why ? <span className="req">(required)</span> : <span className="muted">(optional)</span>}</span></span>
          <textarea className="popup-input" rows={3} value={notes} onChange={(e) => setNotes(e.target.value)} maxLength={2000} placeholder={why ? 'What has come up on site' : 'How much spoil, where the driver can park, a gate code…'} />
        </label>

        <hr className="q-rule" />
        {!ready && <p className="small muted">Answer each question to send.</p>}
        <button type="button" className="btn primary popup-btn" disabled={!ready} onClick={send}>Send request</button>
        <p className="small muted popup-sub">{sup ? `${sup.name} (your supervisor)` : 'Your supervisor'} and operations are emailed straight away. With no signal it sends when there is.</p>
        <button type="button" className="btn popup-btn popup-cancel" onClick={() => { clearDraft(); onClose(); }}>Cancel</button>
      </div>
    </div>
  );
}
